// Global
import React from 'react';

// Local
import { Content } from '@generated/Content/FeatureFancy.model';
import { FeatureCardAlignment, FeatureFancy } from './FeatureFancy';
import { useImageWrapper } from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { useLinkWrapper } from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { Themes } from 'lib/themes';
import { SvgImageWrapper } from 'components/helpers/SitecoreWrappers/SvgImageWrapper/SvgImageWrapper';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';

type FeatureProps = Content.FeatureFancy.FeatureFancy_Component & {
  params: {
    featureContentPosition: FeatureCardAlignment;
    hideDecor?: string;
  };
};

const FeatureDynamic = (props: FeatureProps) => {
  /*
   * Rendering
   */

  const isEditing = useIsEditing();

  // Hooks must be called unconditionally before any early return.
  const image = useImageWrapper({ field: props.fields?.image });
  const iconImage = useImageWrapper({ field: props.fields?.icon });
  const cta1 = useLinkWrapper({
    field: props.fields?.cta1,
    ctaComponentClass: 'component-feature-full-color-cta-1-color',
    ctaStyle: 'filled',
    contentResultAnalytics: {
      contentListName: props.fields?.headline?.value?.trim() || undefined,
    },
  });
  const cta2 = useLinkWrapper({
    field: props.fields?.cta2,
    ctaComponentClass: 'component-feature-full-color-cta-2-color',
    ctaStyle: 'outline',
    contentResultAnalytics: {
      contentListName: props.fields?.headline?.value?.trim() || undefined,
    },
  });
  const eyebrow = usePlainTextWrapper({ field: props.fields?.headlineEyebrow });
  const headline = usePlainTextWrapper({ field: props.fields?.headline });
  const subheadline = usePlainTextWrapper({ field: props.fields?.headlineSubhead });
  const description = useRichTextWrapper({ field: props.fields?.headlineDescription });

  if (!props.fields) {
    return <></>;
  }

  const iconSrc = props.fields?.icon?.value?.src;
  const isSvgIcon = !!iconSrc && iconSrc.includes('.svg');
  const icon = isSvgIcon ? (
    <SvgImageWrapper src={iconSrc || ''} alt={(props.fields?.icon?.value?.alt as string) || ''} />
  ) : (
    iconImage
  );
  const hasCta = !!(props.fields?.cta1?.value?.href || props.fields?.cta2?.value?.href);

  const component = (
    <FeatureFancy
      contentPosition={props.params?.featureContentPosition as FeatureCardAlignment | undefined}
      hideDecor={props.params?.hideDecor === '1'}
      hasCta={hasCta}
      theme={props.params?.theme as Themes}
      image={image}
      icon={icon}
      cta1={cta1}
      cta2={cta2}
      eyebrow={eyebrow}
      headline={headline}
      subheadline={subheadline}
      description={description}
    />
  );

  if (isEditing) {
    return (
      <div>
        <EditingHelpText>This component is going through design changes.</EditingHelpText>
        {component}
      </div>
    );
  }
  return component;
};

export const Default = FeatureDynamic;
export const Full = FeatureDynamic;
export const Standard = FeatureDynamic;
